import React, { useEffect, useState, type ReactNode, useCallback, useRef } from 'react';

import downChevron from '../../assets/images/down-chevron.svg';
import './select.css';

interface Option {
  value: string;
  label: string | ReactNode;
  disabled?: boolean;
}

interface CdSelectProps {
  preset?: string;
  onChange: (value: string) => void;
  children: ReactNode;
  disabled?: boolean;
}

const CdSelect: React.FC<CdSelectProps> = ({ onChange, children, preset, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Normalize options: recursively extract <option> elements from children (including fragments / nested arrays)
  const normalizedOptions: Option[] = (() => {
    const collected: Option[] = [];
    const extractOptions = (node: ReactNode) => {
      React.Children.forEach(node, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type === 'option') {
          const value = child.props.value ?? '';
          const label = child.props.children ?? '';
          const disabledChild = child.props.disabled ?? false;
          collected.push({ value, label, disabled: disabledChild });
        } else if (child.props && child.props.children) {
          extractOptions(child.props.children);
        }
      });
    };
    extractOptions(children);
    return collected;
  })();

  // Preset initial selection
  useEffect(() => {
    if (preset) {
      setSelectedValue(preset);
    }
  }, [preset]);

  const closeMenu = useCallback(() => setOpen(false), []);
  const toggleMenu = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
  }, [disabled]);

  const handleSelect = useCallback(
    (value: string, optionDisabled?: boolean) => {
      if (optionDisabled) return;
      setSelectedValue(value);
      onChange(value);
      closeMenu();
    },
    [onChange, closeMenu],
  );

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!open) return;
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(e.target as Node)
      ) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeMenu]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return;
      const currentIndex = normalizedOptions.findIndex((o) => o.value === selectedValue);
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        let nextIndex = currentIndex === -1 ? 0 : currentIndex + 1;
        while (nextIndex < normalizedOptions.length && normalizedOptions[nextIndex]?.disabled) {
          nextIndex++;
        }
        const nextOpt = normalizedOptions[nextIndex];
        if (nextOpt) {
          handleSelect(nextOpt.value, nextOpt.disabled);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        let prevIndex = currentIndex === -1 ? normalizedOptions.length - 1 : currentIndex - 1;
        while (prevIndex >= 0 && normalizedOptions[prevIndex]?.disabled) {
          prevIndex--;
        }
        const prevOpt = normalizedOptions[prevIndex];
        if (prevOpt) {
          handleSelect(prevOpt.value, prevOpt.disabled);
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        closeMenu();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, normalizedOptions, selectedValue, handleSelect, closeMenu]);

  // Derive visible label
  const selectedLabel = normalizedOptions.find((o) => o.value === selectedValue)?.label || '';
  // Placeholder if exists in options (disabled with empty value)
  const placeholderOption = normalizedOptions.find((o) => o.disabled && o.value === '');
  const displayLabel = selectedLabel || placeholderOption?.label || 'Select an option';

  return (
    <div
      ref={containerRef}
      className="relative inline-block cd-select-container"
    >
      <button
        type="button"
        disabled={disabled}
        onClick={toggleMenu}
        className={`cd-select-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="cd-select-label">{displayLabel}</span>
        <img
          src={downChevron}
          alt="toggle dropdown"
          className={`cd-select-chevron ${open ? 'rotate' : ''}`}
        />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="listbox"
          className="cd-select-menu"
        >
          {normalizedOptions.map((opt) => (
            <div
              key={opt.value + opt.label}
              role="option"
              aria-selected={selectedValue === opt.value}
              aria-disabled={opt.disabled || undefined}
              onClick={() => handleSelect(opt.value, opt.disabled)}
              className={`cd-select-option ${opt.disabled ? 'disabled' : ''}`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CdSelect;
